import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { driverAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Car, Mail, Phone, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DriverProfile = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await driverAPI.getProfile();
      setProfile(response.data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="driver-profile-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Profile
        </h1>

        {/* User Info */}
        <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <User className="w-5 h-5 text-red-600" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-6 mb-6">
              <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-slate-400" />
              </div>
              <div>
                <h2 className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                  {user?.full_name}
                </h2>
                <div className="flex items-center gap-1 text-amber-500">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="font-semibold">{profile?.rating?.toFixed(1) || '5.0'}</span>
                  <span className="text-slate-500 dark:text-slate-400 ml-1">
                    · {profile?.total_rides || 0} rides
                  </span>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label className="text-slate-500 dark:text-slate-400">Email</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-900 dark:text-white">{user?.email}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-500 dark:text-slate-400">Phone</Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-900 dark:text-white">{user?.phone || 'Not set'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Info */}
        {profile && (
          <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                <Car className="w-5 h-5 text-red-600" />
                Vehicle Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center">
                  <Car className="w-10 h-10 text-slate-400" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-slate-900 dark:text-white">
                    {profile.vehicle_year} {profile.vehicle_make} {profile.vehicle_model}
                  </p>
                  <p className="text-slate-500 dark:text-slate-400">
                    {profile.vehicle_color}
                  </p>
                  <p className="font-mono text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {profile.license_plate}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logout */}
        <Button
          onClick={logout}
          variant="outline"
          className="w-full rounded-full border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20 font-semibold"
        >
          Log Out
        </Button>
      </div>
    </div>
  );
};

export default DriverProfile;
